import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../../providers/auth';
import { getAuthStorage } from '../../services/storage';
import wsService from '../../services/whatsapp/websocket';
import { refetchAllChatData } from './useChat';
import { useChatStore } from '../../stores/whatsapp/chatStore';

/**
 * Extrai o GUID do usuario do payload do access token (sub = "guid:sessao_guid").
 *
 * O ContaChat valida o handshake do WS comparando o user_id da URL com esse
 * GUID (main.py, close 4003 se divergir). O objeto user do /perfil/ NAO expoe
 * guid (IUsuario serializa so o id numerico), entao conectar com user.id
 * derruba toda conexao. Extrair do proprio token garante o match por construcao.
 */
function guidFromToken(token: string): string | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const sub = String(payload.sub || '');
    return sub ? sub.split(':')[0] : null;
  } catch {
    return null;
  }
}

// Hook do WebSocket do chat: conecta, reconecta com backoff e faz catch-up das
// mensagens perdidas durante quedas (ver refetchAllChatData).
export function useWebSocket() {
  const [connected, setConnected] = useState(false);
  // reconnecting = já conectou antes e caiu (mostra "reconectando..." na UI).
  const [reconnecting, setReconnecting] = useState(false);
  const { user } = useAuth();
  // Distingue a 1a conexão (já coberta pelo fetch inicial das conversas) de uma
  // reconexão (precisa do catch-up das mensagens perdidas durante a queda).
  const everConnected = useRef(false);

  const handleOpen = useCallback(() => {
    setConnected(true);
    setReconnecting(false);
    useChatStore.getState().setWsReconnecting(false);
    // Catch-up: ao (re)abrir o WS, rebusca conversas + mensagens da conversa
    // ativa. Cobre o gap em que mensagens chegaram com o WS caído (antes só
    // apareciam após F5) e também a 1ª conexão tardia, em que algo chegou entre
    // o fetch inicial e o WS abrir. O fetch extra é barato e idempotente.
    refetchAllChatData();
    everConnected.current = true;
  }, []);
  const handleClose = useCallback(() => {
    setConnected(false);
    // Só sinaliza reconexão se já houve uma conexão antes (evita "reconectando"
    // na primeira carga, quando ainda nem conectou).
    if (everConnected.current) {
      setReconnecting(true);
      useChatStore.getState().setWsReconnecting(true);
    }
  }, []);

  useEffect(() => {
    const userId = (user as any)?.guid || (user as any)?.id;
    if (!userId) return undefined;

    wsService.onOpen(handleOpen);
    wsService.onClose(handleClose);
    // Resolver chamado a CADA (re)conexão: lê o access_token atualizado do
    // storage (renovado pelo refresh do axios) — assim a reconexão nunca usa um
    // token velho. O GUID do token tem prioridade (o backend compara com o sub).
    wsService.start(async () => {
      const storageAuth = await getAuthStorage();
      const accessToken = storageAuth?.access_token || '';
      if (!accessToken) return null;
      const wsUserId = guidFromToken(accessToken) || userId;
      return { userId: String(wsUserId), token: accessToken };
    });

    return () => {
      wsService.offOpen(handleOpen);
      wsService.offClose(handleClose);
      wsService.disconnect();
      setConnected(false);
    };
  }, [user, handleOpen, handleClose]);

  return { connected, reconnecting };
}
